import React, { useCallback, useRef, useState } from 'react';
import { Nullable } from 'common/types/nullable';
import AsyncValueUtils from './AsyncValueUtils';
import { AsyncValueState } from 'common/providers/AsyncValues/types';

/**
 * Hook for async data fetching with intelligent caching and duplicate request prevention.
 * • Prevents multiple concurrent requests to the same resource by reusing in-flight promises.
 * • Safely handles component unmounting to prevent memory leaks from setState calls.
 *
 * @param fetchFunction - Async function that returns the data to fetch
 * @returns Returns cached value immediately if available, otherwise fetches fresh data
 */
export const useAsyncValue = <T>(
  fetchFunction: () => Promise<T>,
): AsyncValueState<T> & {
  refetch: () => Promise<Nullable<T>>;
} => {
  const [state, setState] = useState<AsyncValueState<T>>(AsyncValueUtils.createInitialState<T>());

  const isMountedRef = useRef(true);
  const inFlightRequestRef = useRef<Promise<Nullable<T>> | null>(null) as React.MutableRefObject<Promise<
    Nullable<T>
  > | null>;

  // Track component mount status and cleanup
  React.useEffect(
    () => () => {
      isMountedRef.current = false;
      // Cancel any in-flight requests when component unmounts
      if (inFlightRequestRef.current) {
        inFlightRequestRef.current = null;
      }
    },
    [],
  );

  /** Fetches data with caching and in-flight request tracking */
  const refetch = useCallback(async (): Promise<Nullable<T>> => {
    // Don't start new requests if component is unmounted
    if (!isMountedRef.current) {
      return null;
    }

    if (state.value !== null) return state.value;

    return await (inFlightRequestRef.current ||
      AsyncValueUtils.fetchState(isMountedRef, setState, fetchFunction, inFlightRequestRef));
  }, [fetchFunction, state.value]);

  return { ...state, refetch };
};
